/**
 * Wrappers tipados para /api/config (§9 SRS) y /api/files/sign.
 */

import { apiClient } from './apiClient'
import { signFile, type SignedUrl } from './fileSigner'

export type { SignedUrl }

export type TenantConfig = {
  id: number
  company_name: string
  folio_prefix: string
  current_sequence: number
  /** Preview del próximo folio basado en prefix + current_sequence + 1. */
  next_folio_preview: string
  logo_path: string | null
  signature_path: string | null
  signature_enabled: boolean
  client_signature_enabled: boolean
  responsible_name_enabled: boolean
  page_size: 'LETTER' | 'A4'
  // Visibilidad de encabezados del PDF
  show_company_name: boolean
  show_folio: boolean
  // Visibilidad de datos básicos en el PDF
  show_client: boolean
  show_rfc: boolean
  show_address: boolean
  show_system: boolean
  show_building: boolean
  show_order: boolean
  show_city: boolean
  show_dates: boolean
  updated_at: string
}

export type ConfigUpdatePayload = Partial<{
  company_name: string
  folio_prefix: string
  current_sequence: number
  signature_enabled: boolean
  client_signature_enabled: boolean
  responsible_name_enabled: boolean
  page_size: 'LETTER' | 'A4'
  show_company_name: boolean
  show_folio: boolean
  show_client: boolean
  show_rfc: boolean
  show_address: boolean
  show_system: boolean
  show_building: boolean
  show_order: boolean
  show_city: boolean
  show_dates: boolean
}>

export type TenantPlan = {
  id: number
  name: string
  description: string | null
  status: 'active' | 'pending' | 'past_due' | 'canceled'
  current_period_end: string | null
  billing_type: 'monthly' | 'yearly'
  max_reports_per_month: number
  max_ai_enhancements_per_month: number
  max_clients: number
  /**
   * Si false, el tenant no tiene la feature de "mejorar redacción" en su plan.
   * El frontend oculta el botón en ese caso.
   */
  ai_enhancement_enabled: boolean
}

export type TenantUsage = {
  reports_this_month: number
  ai_enhancements_this_month: number
}

export type ConfigResponse = {
  config: TenantConfig
  plan: TenantPlan | null
  usage: TenantUsage
}

export const configClient = {
  show: () => apiClient.get<ConfigResponse>('/config'),

  update: (payload: ConfigUpdatePayload) =>
    apiClient.patch<{ config: TenantConfig }>('/config', payload),

  uploadLogo: (file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return apiClient.post<{ config: TenantConfig }>('/config/logo', fd)
  },

  deleteLogo: () => apiClient.delete<{ config: TenantConfig }>('/config/logo'),

  uploadSignature: (file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return apiClient.post<{ config: TenantConfig }>('/config/firma', fd)
  },

  deleteSignature: () => apiClient.delete<{ config: TenantConfig }>('/config/firma'),

  /**
   * Pide un token firmado para servir un archivo privado vía <img src={signed.url} />.
   * Agrupa peticiones en lotes y cachea hasta la expiración (ver fileSigner.ts).
   */
  signFile: (path: string) => signFile(path),
}

// ─── Validadores client-side ────────────────────────────────────────────────

export const LOGO_MAX_BYTES = 2 * 1024 * 1024
export const SIGNATURE_MAX_BYTES = 1 * 1024 * 1024
export const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'] as const

export function validateImageFile(file: File, maxBytes: number): string | null {
  if (!ALLOWED_IMAGE_TYPES.includes(file.type as (typeof ALLOWED_IMAGE_TYPES)[number])) {
    return 'Formato no permitido. Usa PNG, JPG o WEBP.'
  }
  if (file.size > maxBytes) {
    const maxMb = Math.ceil(maxBytes / 1024 / 1024)
    return `El archivo excede el tamaño máximo (${maxMb} MB).`
  }
  return null
}
